import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { clamp } from 'lodash';
import type { SidebarAlignment } from './types';

export type WidthDragResize = {
  minWidth: number;
  maxWidth: number;
  onResizeEnd: (width: number) => void;
};

type WidthDragOptions = {
  panelRef: RefObject<HTMLElement | null>;
  alignment: SidebarAlignment;
  resize: WidthDragResize | undefined;
};

type WidthDrag = {
  dragWidth: number | null;
  isDragging: boolean;
  startDrag: VoidFunction;
};

const toDraggedWidth = (
  panel: HTMLElement,
  alignment: SidebarAlignment,
  clientX: number
): number => {
  const { left, right } = panel.getBoundingClientRect();
  return alignment === 'left' ? clientX - left : right - clientX;
};

/** Drags the inner edge of a panel; the width only lives here until the drag ends. */
export const useWidthDrag = ({
  panelRef,
  alignment,
  resize,
}: WidthDragOptions): WidthDrag => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const dragWidthRef = useRef<number | null>(null);
  const resizeRef = useRef(resize);

  useLayoutEffect(() => {
    resizeRef.current = resize;
  });

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (event: MouseEvent) => {
      const panel = panelRef.current;
      const current = resizeRef.current;
      if (!panel || !current) return;
      event.preventDefault();
      const width = clamp(
        toDraggedWidth(panel, alignment, event.clientX),
        current.minWidth,
        current.maxWidth
      );
      dragWidthRef.current = width;
      setDragWidth(width);
    };
    const onMouseUp = () => {
      const width = dragWidthRef.current;
      dragWidthRef.current = null;
      setIsDragging(false);
      setDragWidth(null);
      if (width !== null) resizeRef.current?.onResizeEnd(width);
    };
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, alignment, panelRef]);

  return { dragWidth, isDragging, startDrag: () => setIsDragging(true) };
};
